import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Scale, Gem } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/site";

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden pt-[164px] pb-24 lg:pt-[196px] lg:pb-32">
      <img
        src="/assets/texas-glow.jpg"
        alt=""
        aria-hidden="true"
        data-testid="hero-texas-bg"
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] lg:w-[1400px] opacity-20 pointer-events-none select-none [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_72%)]"
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.span
            {...fade(0)}
            className="font-display text-xs md:text-sm uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-6 block"
          >
            Texas Fine Jewelry &middot; Watches &middot; Diamonds
          </motion.span>

          <motion.h1
            {...fade(0.1)}
            data-testid="hero-headline"
            className="font-display text-5xl md:text-6xl lg:text-7xl font-normal tracking-tight text-neutral-900 leading-[1.05]"
          >
            Crafted for eternity. <span className="text-[#C9A227]">Curated for you.</span>
          </motion.h1>

          <motion.p {...fade(0.2)} data-testid="hero-subcopy" className="mt-8 text-base md:text-lg leading-relaxed text-neutral-600 max-w-xl">
            Tens of thousands of fine jewelry styles, certified diamonds, and luxury timepieces across our
            three Texas showrooms — from a family business owned and operated since 1991,
            with honest valuations and everything tested right in front of you.
          </motion.p>

          <motion.div {...fade(0.3)} className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/catalog"
              data-testid="hero-cta-catalog"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-8 py-3.5 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_20px_40px_rgb(201,162,39,0.20)]"
            >
              Browse the Catalog <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
            <Link
              to="/sell"
              data-testid="hero-cta-sell"
              className="inline-flex items-center gap-2 rounded-full border border-neutral-900 text-neutral-900 text-sm font-semibold px-8 py-3.5 hover:bg-neutral-900 hover:text-white transition-colors duration-300"
            >
              Sell Your Gold
            </Link>
          </motion.div>

          <motion.div {...fade(0.4)} className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
            {[
              { icon: Gem, text: "Tens of thousands of styles — gold, diamonds & watches" },
              { icon: Scale, text: "Live counter valuations, weighed in front of you" },
              { icon: BadgeCheck, text: "3 Texas showrooms — Katy, Copperas Cove & Killeen" },
            ].map(({ icon: Icon, text }, i) => (
              <div key={i} data-testid={`hero-proof-${i}`} className="flex items-start gap-3">
                <Icon size={20} strokeWidth={1} className="text-[#C9A227] mt-0.5 shrink-0" />
                <span className="text-sm text-neutral-600 leading-snug">{text}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="absolute -top-6 -left-6 w-full h-full border border-[#D4AF37]/40 rounded-sm pointer-events-none" />
          <div className="relative overflow-hidden rounded-sm shadow-[0_20px_40px_rgb(184,134,11,0.08)]">
            <img
              src={IMAGES.hero}
              alt="Couple wearing a gold chain, diamond ring and luxury watches"
              data-testid="hero-image"
              className="w-full h-[420px] lg:h-[560px] object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-5 left-5 text-white">
              <p className="text-xs uppercase tracking-[0.2em] opacity-80">Since 1991</p>
              <p className="font-display text-lg">Family owned, community trusted</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
